/**
 * 聊天记录 - 接口定义
 */
import { apiGet, apiPost, apiDownload } from '@/utils/request';
import { parseListResponse, parsePaginatedResponseFull } from '@/utils/apiResponse';
import { DEFAULT_PAGE_SIZE } from '@/constants/api';
import type {
  UpdateUserParams,
  LeadValidityChannelGroup,
  AccountUnreadResult,
} from '@/types/chatRecords';

/** 导出字段项 */
export interface ExportFieldItem {
  key: string;
  label: string;
  required?: boolean;
}

export const chatRecordsApi = {
  list: (params: Record<string, unknown> = {}) =>
    apiGet('/chat-records', { per_page: DEFAULT_PAGE_SIZE, page: 1, ...params }).then((d) =>
      parsePaginatedResponseFull(d, { page: 1, per_page: DEFAULT_PAGE_SIZE }),
    ),
  accounts: (params?: Record<string, unknown>) =>
    apiGet('/chat-records/accounts', params).then((d) => parseListResponse(d)),
  /**
   * 各渠道账号的未读消息数，供账号列表徽标使用。
   * 账号范围口径与 accounts 完全一致，参数同名同义可原样透传；
   * 不传则统计当前坐席可见的全部账号。
   */
  accountsUnread: (params?: Record<string, unknown>) =>
    apiGet<AccountUnreadResult>('/chat-records/accounts/unread', params),
  export: (params: Record<string, unknown>) =>
    apiPost<{ url: string }>('/chat-records/export', params),
  exportTemplate: (filename?: string) =>
    apiDownload('/chat-records/export-template', filename ? { filename } : {}),
  importValidity: (rows: unknown[]) => apiPost('/chat-records/import-validity', { rows }),
  /** 获取导出可选字段列表 */
  exportFields: () => apiGet<{ fields: ExportFieldItem[] }>('/chat-records/export-fields'),
  /** 发送导出验证码 */
  sendExportCode: (phone: string) => apiGet('/chat-records/send-code', { phone }),
  /** 更新用户信息 */
  updateUser: (params: UpdateUserParams) => apiPost('/chat-records/update-user', params),
  /** 获取所有渠道的客资有效性可选项（按渠道分组） */
  leadValidityOptions: () =>
    apiGet<{ channels: LeadValidityChannelGroup[] }>('/channels/lead-validity-options', {
      channel_key: 'all',
    }),
};

/** 公开链接返回数据 */
export interface PublicLinkData {
  id?: number;
  token?: string;
  params?: Record<string, unknown>;
  effect_time?: number;
  duration?: number;
}

/** 公开链接接口 */
export const publicLinksApi = {
  /** 生成公开访问链接 */
  create: (key: string, params: Record<string, unknown>, duration = 14) =>
    apiPost<{ data: PublicLinkData }>('/public-links', { key, params, duration }),
};
